import React, { useEffect, useState } from "react";
import { Save, RotateCcw, Palette } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, Loader } from "@/components/dashboard/ui";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

function hexToHsl(hex) {
  let r = 0, g = 0, b = 0;
  hex = hex.replace("#", "");
  if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
  r = parseInt(hex.slice(0, 2), 16) / 255; g = parseInt(hex.slice(2, 4), 16) / 255; b = parseInt(hex.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2; else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
function hslToHex(hsl) {
  if (!hsl) return "#000000";
  const [h, s, l] = hsl.replace(/%/g, "").split(" ").map(Number);
  const a = s / 100, ll = l / 100;
  const k = (n) => (n + h / 30) % 12;
  const f = (n) => ll - a * Math.min(ll, 1 - ll) * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

const COLORS = [
  ["primary", "Couleur primaire"], ["secondary", "Couleur secondaire"], ["accent", "Couleur accent"],
  ["background", "Arrière-plan"], ["surface", "Surfaces & cartes"], ["foreground", "Texte principal"],
  ["muted_foreground", "Texte secondaire"], ["border", "Bordures"], ["sidebar_bg", "Fond sidebar"], ["sidebar_text", "Texte sidebar"],
];
const SIZES = [["h1_size", "Taille H1"], ["h2_size", "Taille H2"], ["h3_size", "Taille H3"], ["radius", "Rayon des bordures"]];
const DARK_COLORS = [
  ["dark_section_bg", "Fond sections foncées"], ["dark_section_text", "Texte sections foncées"], ["dark_section_muted", "Texte secondaire sections foncées"],
  ["footer_bg", "Fond du footer"], ["footer_text", "Texte du footer"], ["footer_muted", "Texte secondaire footer"],
];

export default function AdminDesign() {
  const { theme, setTheme, applyTheme, reload } = useTheme();
  const [draft, setDraft] = useState(null);
  useEffect(() => { if (theme) setDraft({ ...theme }); }, [theme]);

  const change = (k, v) => { const d = { ...draft, [k]: v }; setDraft(d); applyTheme(d); };
  const setColor = (k, hex) => change(k, hexToHsl(hex));

  const save = async () => {
    try { const { data } = await api.put("/theme", draft); setTheme(data); applyTheme(data); toast.success("Thème enregistré"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const reset = async () => {
    try { const { data } = await api.post("/theme/reset"); setTheme(data); setDraft({ ...data }); applyTheme(data); toast.success("Thème par défaut restauré"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const cancel = () => { setDraft({ ...theme }); applyTheme(theme); toast("Modifications annulées"); };

  if (!draft) return <Loader />;
  return (
    <div>
      <PageHeader title="Design & thème" subtitle="Personnalisez l'identité visuelle. L'aperçu est instantané."
        action={<div className="flex gap-2">
          <Button variant="outline" onClick={cancel} data-testid="theme-cancel"><RotateCcw className="h-4 w-4 mr-1.5" />Annuler</Button>
          <AlertDialog><AlertDialogTrigger asChild><Button variant="outline" data-testid="theme-reset">Réinitialiser</Button></AlertDialogTrigger>
            <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Restaurer le thème par défaut ?</AlertDialogTitle><AlertDialogDescription>Toutes vos personnalisations visuelles seront remplacées par le thème d'origine.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={reset}>Restaurer</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
          <Button className="bg-primary" onClick={save} data-testid="theme-save"><Save className="h-4 w-4 mr-1.5" />Enregistrer</Button>
        </div>} />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Palette className="h-4 w-4 text-primary" />Couleurs</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {COLORS.map(([k, label]) => (
                <div key={k} className="flex items-center gap-3">
                  <input type="color" value={hslToHex(draft[k])} onChange={(e) => setColor(k, e.target.value)} className="h-9 w-12 rounded cursor-pointer border border-slate-200" data-testid={`color-${k}`} />
                  <span className="text-sm text-slate-600">{label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2"><Palette className="h-4 w-4 text-primary" />Sections foncées & footer</h3>
            <p className="text-xs text-slate-400 mb-4">Couleurs de la section « Pourquoi externaliser » et du pied de page.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DARK_COLORS.map(([k, label]) => (
                <div key={k} className="flex items-center gap-3">
                  <input type="color" value={hslToHex(draft[k])} onChange={(e) => setColor(k, e.target.value)} className="h-9 w-12 rounded cursor-pointer border border-slate-200" data-testid={`color-${k}`} />
                  <span className="text-sm text-slate-600">{label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Typographie & espacement</h3>
            <div className="grid gap-3">
              <div><Label className="text-xs">Police des titres</Label><Input value={draft.font_heading} onChange={(e) => change("font_heading", e.target.value)} className="mt-1" /></div>
              <div><Label className="text-xs">Police du corps</Label><Input value={draft.font_body} onChange={(e) => change("font_body", e.target.value)} className="mt-1" /></div>
              {SIZES.map(([k, label]) => (
                <div key={k}><Label className="text-xs">{label}</Label><Input value={draft[k]} onChange={(e) => change(k, e.target.value)} className="mt-1" data-testid={`size-${k}`} /></div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:sticky lg:top-24 h-fit">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-100 text-xs font-semibold text-slate-400 uppercase tracking-wider">Aperçu</div>
            <div className="p-6" style={{ background: "hsl(var(--background))" }}>
              <h1 className="font-heading font-bold text-slate-900" style={{ fontSize: "var(--h1-size)", lineHeight: 1.1 }}>Delya</h1>
              <p className="mt-2 text-muted-foreground">Votre secrétariat médical à distance.</p>
              <div className="mt-4 flex gap-2">
                <Button className="bg-primary">Bouton primaire</Button>
                <Button variant="secondary">Secondaire</Button>
                <Button variant="outline">Contour</Button>
              </div>
              <div className="mt-4 rounded-lg border p-4" style={{ background: "hsl(var(--surface))" }}>
                <p className="font-semibold text-slate-900">Carte d'exemple</p>
                <p className="text-sm text-muted-foreground mt-1">Prévisualisation des surfaces et bordures.</p>
                <span className="inline-block mt-3 px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent">Accent</span>
              </div>
              <div className="mt-4 rounded-lg p-4 text-sm" style={{ background: "hsl(var(--sidebar-bg))", color: "hsl(var(--sidebar-text))" }}>Aperçu de la barre latérale</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
